import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Chapter VII — WORK WITH US
 *
 * Final section of the manuscript. Split out from the old ChapterArchive
 * so the visitor reaches a clean, focused inquiry block at the end of
 * the journey. Two paths: Raw Material partnership or Private Label.
 * Qualifier questions, contact form, submits to POST /api/contact.
 */
export default function ChapterWorkWithUs() {
  const { t, lang } = useI18n();

  const [path, setPath] = useState("raw_material");
  const [q, setQ] = useState({ master: null, custom: null, brand: null });
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("work.error"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        path,
        has_master_blender: q.master,
        needs_custom_dev: q.custom,
        new_brand: q.brand,
      });
      toast.success(t("work.success"));
      setForm({ name: "", email: "", company: "", country: "", message: "" });
      setQ({ master: null, custom: null, brand: null });
    } catch (_err) {
      toast.error(t("work.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const epigraphEN =
    "Ready to build with us? Two ways to begin a Ramírez partnership.";
  const epigraphES =
    "¿Listo para construir con nosotros? Dos caminos para iniciar una alianza con Ramírez.";

  return (
    <section
      id="work"
      data-testid="chapter-work"
      className="relative bg-pearl-warm text-tobacco py-20 md:py-24 border-t border-tobacco/10"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="font-mono text-[10px] md:text-[10.5px] tracking-widest2 uppercase text-tobacco/65 mb-5">
              {t("work.eyebrow") || "Chapter VII — Work With Us"}
            </div>
            <h2 className="font-display italic text-tobacco leading-[0.98] text-[40px] md:text-[52px] lg:text-[60px] font-normal max-w-[18ch]">
              {t("work.title")}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-tobacco/85 text-[15px] md:text-[15.5px] leading-[1.7] font-normal max-w-[46ch]">
              {lang === "es" ? epigraphES : epigraphEN}
            </p>
          </div>
        </div>

        {/* Two path selector */}
        <div className="flex flex-wrap gap-3 mb-10" data-testid="work-paths">
          {[
            {
              k: "raw_material",
              code: "I",
              t_en: "Raw Material — wholesale leaf supply",
              t_es: "Materia Prima — venta de hoja al por mayor",
            },
            {
              k: "private_label",
              code: "II",
              t_en: "Private Label — your brand, our craft",
              t_es: "Marca Privada — tu marca, nuestro oficio",
            },
          ].map((p) => (
            <button
              key={p.k}
              type="button"
              onClick={() => setPath(p.k)}
              data-testid={`path-${p.k}`}
              className={`flex items-baseline gap-3 px-5 py-3 border transition-colors duration-700 ${
                path === p.k
                  ? "border-tobacco bg-tobacco text-pearl"
                  : "border-tobacco/30 text-tobacco hover:border-tobacco/60"
              }`}
            >
              <span className="font-mono text-[10px] tracking-widest2 uppercase opacity-75">
                Path · {p.code}
              </span>
              <span className="font-mono text-[11px] tracking-widest2 uppercase font-medium">
                {lang === "es" ? p.t_es : p.t_en}
              </span>
            </button>
          ))}
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmit}
          data-testid="work-form"
          className="grid grid-cols-12 gap-x-6 gap-y-5"
        >
          <Field
            className="col-span-12 md:col-span-3"
            label={t("work.name")}
            name="name"
            form={form}
            setForm={setForm}
            required
          />
          <Field
            className="col-span-12 md:col-span-3"
            label={t("work.email")}
            name="email"
            type="email"
            form={form}
            setForm={setForm}
            required
          />
          <Field
            className="col-span-12 md:col-span-3"
            label={t("work.company")}
            name="company"
            form={form}
            setForm={setForm}
          />
          <Field
            className="col-span-12 md:col-span-3"
            label={t("work.country")}
            name="country"
            form={form}
            setForm={setForm}
          />

          <div className="col-span-12">
            <label className="font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55 block mb-1.5">
              {t("work.message")} *
            </label>
            <textarea
              required
              rows={3}
              value={form.message}
              onChange={(e) =>
                setForm((f) => ({ ...f, message: e.target.value }))
              }
              className="input-line resize-none text-[14px]"
              data-testid="work-input-message"
            />
          </div>

          {/* Qualifier questions */}
          <div className="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-5 mt-2">
            {[
              { k: "master", q: t("work.q1") },
              { k: "custom", q: t("work.q2") },
              { k: "brand", q: t("work.q3") },
            ].map((item) => (
              <div key={item.k} className="flex flex-col gap-2">
                <span className="font-mono text-[9px] tracking-widest2 uppercase text-tobacco/65">
                  {item.q}
                </span>
                <div className="flex gap-2">
                  {[
                    { v: true, label: t("work.yes") },
                    { v: false, label: t("work.no") },
                  ].map((opt) => (
                    <button
                      key={String(opt.v)}
                      type="button"
                      onClick={() =>
                        setQ((s) => ({ ...s, [item.k]: opt.v }))
                      }
                      data-testid={`qualifier-${item.k}-${
                        opt.v ? "yes" : "no"
                      }`}
                      className={`px-3 py-1 border font-mono text-[9px] tracking-widest2 uppercase transition-colors duration-700 ${
                        q[item.k] === opt.v
                          ? "border-tobacco bg-tobacco text-pearl"
                          : "border-tobacco/25 text-tobacco/65 hover:border-tobacco/50"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="col-span-12 flex flex-wrap items-center justify-between gap-4 mt-3">
            <p className="font-mono text-[9.5px] tracking-widest2 uppercase text-tobacco/55 max-w-[48ch]">
              {lang === "es"
                ? "Respondemos personalmente a cada consulta dentro de cinco días laborables."
                : "We reply to every inquiry personally within five business days."}
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="btn-luxe-solid disabled:opacity-50 disabled:cursor-wait"
              data-testid="work-submit"
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={submitting ? "sending" : "send"}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {submitting ? t("work.submitting") : t("work.submit")}
                </motion.span>
              </AnimatePresence>
              <span className="arrow-tick" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", form, setForm, required, className = "" }) {
  return (
    <div className={className}>
      <label className="font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55 block mb-1.5">
        {label}
        {required ? " *" : ""}
      </label>
      <input
        type={type}
        required={required}
        value={form[name]}
        onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
        className="input-line text-[14px]"
        data-testid={`work-input-${name}`}
      />
    </div>
  );
}
